/**
 * Shared chart theme for Recharts surfaces across the app — keeps all
 * chart colors, axis styles, grid treatments, and tooltip rendering
 * locked to the "seared metal" design language so the Commander panes
 * and deck panes look like they belong to the same product.
 */

import React from 'react';
import { ROLE_COLORS, roleColor } from './roleColorMap';

// ---- Core palette ----------------------------------------------------
export const CHART_ACCENT_RED = '#cc1a1a';
export const CHART_ACCENT_RED_DEEP = '#96000d';
export const CHART_ACCENT_GOLD = '#e8c600';
export const CHART_ACCENT_GOLD_WARM = '#ffd400';
export const CHART_INK_MUTED = '#8b93a7';
export const CHART_INK_DIM = '#6a7288';
export const CHART_INK = '#ffffff';

// Role palette lives in roleColorMap (single source of truth) — re-
// exported here so chart code can keep importing from one place.
export { ROLE_COLORS as CHART_ROLE_COLORS, roleColor };

// Archetype palette — three distinct hues pulled from the seared theme
// instead of the old neon trio. COMBO gets red (aggression), CONTROL the
// muted blue, MIDRANGE a warm gold so they read as related but distinct.
export const CHART_ARCHETYPE_COLORS = {
    COMBO: '#cc1a1a',
    CONTROL: '#4a9bd6',
    MIDRANGE: '#e8c600',
};

// MTG mana colors — match the Commander ManabaseTab palette so a player's
// "blue spells" look like blue spells everywhere in the app.
export const CHART_MANA_COLORS = {
    w: '#f5f3e3',
    u: '#4a9bd6',
    b: '#8a7a9e',
    r: '#d6534a',
    g: '#5fa85a',
    c: '#9a9a9a',
};

// ---- Grid + axis defaults --------------------------------------------
export const CHART_GRID_STROKE = 'rgba(255, 255, 255, 0.06)';
export const CHART_GRID_STROKE_DASH = '3 3';

export const CHART_AXIS_STYLE = {
    stroke: CHART_INK_MUTED,
    tickLine: false,
    tick: {
        fill: CHART_INK_MUTED,
        fontSize: 11,
        fontFamily: "'Courier New', Courier, monospace",
        letterSpacing: 1,
    },
};

export const CHART_LABEL_STYLE = {
    fill: CHART_INK_MUTED,
    fontSize: 11,
    fontFamily: "'Courier New', Courier, monospace",
    letterSpacing: 1,
};

// ---- Legend styling --------------------------------------------------
// Recharts' default Legend wraps items in a `<ul>` with user-agent
// defaults; we force the whole block into our seared typography via
// `wrapperStyle` so every chart's legend reads like a chip row.
export const CHART_LEGEND_WRAPPER_STYLE = {
    paddingTop: 10,
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.25,
    textTransform: 'uppercase',
    color: CHART_INK_MUTED,
};

export const searedLegendFormatter = (value, entry) => (
    <span
        style={{
            color: entry?.color ? CHART_INK_MUTED : CHART_INK_MUTED,
            fontFamily: "'Courier New', Courier, monospace",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.25,
            textTransform: 'uppercase',
            marginRight: 6,
        }}
    >
        {typeof value === 'string' ? value : String(value)}
    </span>
);

// ---- Gradient id helpers ---------------------------------------------
// Recharts gradients need unique ids or the browser caches the first
// one rendered. Use this helper inside a `<linearGradient id={...}>`.
export const gradientId = (name) => `chart-gradient-${name}`;

/**
 * Recharts only renders children it recognizes (Bar, XAxis, Tooltip,
 * ...) — arbitrary wrapper components get dropped from the SVG output.
 * These helpers return the `<linearGradient>` directly so callers can
 * drop them inside an inline `<defs>` child of the chart.
 *
 *   <BarChart>
 *     <defs>{searedBarGradient({ id: 'power-score' })}</defs>
 *     ...
 *     <Bar fill="url(#power-score)" />
 *   </BarChart>
 */
export const searedBarGradient = ({ id = gradientId('bar'), from = CHART_ACCENT_GOLD_WARM, to = CHART_ACCENT_RED }) => (
    <linearGradient id={id} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor={from} stopOpacity={0.95} />
        <stop offset="100%" stopColor={to} stopOpacity={0.95} />
    </linearGradient>
);

export const searedAreaGradient = ({ id = gradientId('area'), color = CHART_ACCENT_GOLD }) => (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={0.45} />
        <stop offset="100%" stopColor={color} stopOpacity={0.05} />
    </linearGradient>
);

// Back-compat aliases — older call sites still use the capitalized
// component form. Both render the same `<linearGradient>`, but the
// new callers should wrap in `<defs>{...}</defs>`.
export const SearedBarGradient = (props) => <defs>{searedBarGradient(props)}</defs>;
export const SearedAreaGradient = (props) => <defs>{searedAreaGradient(props)}</defs>;

// ---- Tooltip ---------------------------------------------------------
/**
 * Shared tooltip content renderer for Recharts. Use as
 * `<Tooltip content={<SearedTooltip />} cursor={SEARED_TOOLTIP_CURSOR} />`.
 *
 * Styling mirrors `.panelGradient` — flat dark surface with a red
 * left accent stripe — so tooltips read as pop-ups from the same
 * visual family as the panels behind them.
 *
 * Props injected by Recharts: `active`, `payload`, `label`. Optional
 * `labelFormatter` / `valueFormatter` props can be passed through for
 * custom formatting.
 */
export function SearedTooltip({ active, payload, label, labelFormatter, valueFormatter, title, hideLabel }) {
    if (!active || !payload || payload.length === 0) return null;

    const resolvedLabel =
        title != null ? title : typeof labelFormatter === 'function' ? labelFormatter(label) : label;

    return (
        <div
            style={{
                background: 'linear-gradient(180deg, #141414 0%, #0e0e0e 100%)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderLeft: `2px solid ${CHART_ACCENT_RED}`,
                borderRadius: '0 3px 3px 0',
                padding: '8px 10px',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.55)',
                minWidth: 120,
                fontFamily: 'Arial, sans-serif',
            }}
        >
            {!hideLabel && resolvedLabel != null && (
                <div
                    style={{
                        color: CHART_INK_MUTED,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: 'uppercase',
                        fontFamily: "'Courier New', Courier, monospace",
                        marginBottom: 4,
                    }}
                >
                    {resolvedLabel}
                </div>
            )}
            {payload.map((entry, idx) => {
                const color = entry?.color || entry?.payload?.fill || CHART_ACCENT_GOLD;
                const rawValue = entry?.value;
                const display =
                    typeof valueFormatter === 'function'
                        ? valueFormatter(rawValue, entry)
                        : rawValue;
                return (
                    <div
                        key={`tooltip-${idx}`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontSize: 12,
                            color: '#ffffff',
                            lineHeight: 1.4,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        <span
                            style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                borderRadius: 1,
                                background: color,
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ color: CHART_INK_MUTED, flex: 1 }}>{entry?.name}</span>
                        <span style={{ fontWeight: 700 }}>{display}</span>
                    </div>
                );
            })}
        </div>
    );
}

// Recharts' default cursor is a grey block — subtle red tint matches
// the seared theme.
export const SEARED_TOOLTIP_CURSOR = {
    fill: 'rgba(204, 26, 26, 0.08)',
    stroke: 'rgba(204, 26, 26, 0.25)',
    strokeWidth: 1,
};

// ---- Misc helpers ----------------------------------------------------
/**
 * Build a continuous HSL stop from red→yellow→green, keyed to a 0–100
 * intensity value. Used by SaltPane's nail segments so saltier decks
 * stay red, chiller decks bleed toward gold/green.
 */
export const intensityHue = (intensity) => {
    const clamped = Math.max(0, Math.min(100, intensity));
    // 0 → deep red (hue 0), 100 → gold/green (hue ~70)
    return Math.round(70 - (clamped / 100) * 70);
};

export const intensityColor = (intensity, saturation = 85, lightness = 48) =>
    `hsl(${intensityHue(100 - intensity)} ${saturation}% ${lightness}%)`;
